import { useLocation, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Shield, Crosshair, Crown, Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StickyWhatsApp from "@/components/StickyWhatsApp";
import { TIERS, CASE_STUDIES, FAQS, formatINR } from "@/data/content";
import { openWhatsApp } from "@/lib/api";

const ICONS = { Shield, Crosshair, Crown, Star };

const HERO_COPY = {
  drona: {
    eyebrow: "Haven't chosen your car yet?",
    headline: "Let us help you decide.",
    sub: "You don't need another YouTube review. You need a clear, written recommendation on what to buy and from whom.",
  },
  arjuna: {
    eyebrow: "Already chosen your car?",
    headline: "Let us negotiate the deal.",
    sub: "You don't need another review. You need someone on your side of the table.",
  },
  bhishma: {
    eyebrow: "Want negotiation and protection?",
    headline: "We'll stay with you till delivery.",
    sub: "Negotiation, a 60+ point remote PDI, document checks and on-call support — from quote to keys.",
  },
};

export default function TierPage() {
  const location = useLocation();
  const tierId = location.pathname.replace("/", "");
  const tier = TIERS.find((t) => t.id === tierId);
  if (!tier || tier.comingSoon) return <Navigate to="/" replace />;

  const Icon = ICONS[tier.icon] || Star;
  const copy = HERO_COPY[tier.id];
  const relevantCases = CASE_STUDIES.slice(0, 2);
  const relevantFaqs = FAQS.filter((f) =>
    f.q.toLowerCase().includes(tier.id) ||
    f.q.toLowerCase().includes("drona") ||
    f.q.toLowerCase().includes("arjuna") ||
    f.q.toLowerCase().includes("bhishma") ||
    f.q.toLowerCase().includes("savings") ||
    f.q.toLowerCase().includes("accept")
  ).slice(0, 5);

  return (
    <main className="relative bg-void text-silver" data-testid="tier-page">
      <Navbar />

      <section className="relative pt-40 md:pt-48 pb-20 md:pb-28 bg-void grain" data-testid="tier-hero">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full glass gradient-border"
          >
            <span className="w-2 h-2 rounded-full gradient-bg wa-pulse flex-shrink-0" />
            <span className="eyebrow !text-[13px] gradient-text font-bold">{copy.eyebrow}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.1 }}
            className="mt-6 font-serif text-white text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight"
          >
            {copy.headline}
          </motion.h1>

          <p className="mt-6 text-lg md:text-xl text-silver/75 max-w-2xl leading-relaxed">{copy.sub}</p>

          <div className="mt-8 flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl grid place-items-center bg-white/5 border border-white/10" style={{ color: tier.color }}>
              <Icon className="w-7 h-7" />
            </div>
            <div>
              <p className="eyebrow" style={{ color: tier.color }}>{tier.category}</p>
              <p className="font-serif text-3xl text-white leading-none">{tier.name}</p>
            </div>
          </div>

          {tier.forWhen && (
            <p className="mt-8 font-serif italic text-xl md:text-2xl text-white/90 max-w-2xl leading-snug border-l-2 pl-5" style={{ borderColor: tier.color }}>
              {tier.forWhen}
            </p>
          )}

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <span className="font-serif text-3xl md:text-4xl text-white">{tier.priceLabel}</span>
            <button
              onClick={() => openWhatsApp({ source: `tier_page_${tier.id}`, tier: tier.name })}
              className="inline-flex items-center gap-2.5 px-7 py-4 rounded-full text-sm md:text-base font-medium text-white gradient-bg shadow-[0_20px_60px_-15px_rgba(139,92,246,0.6)] hover:scale-[1.03] transition-transform"
              data-testid="tier-page-cta"
            >
              {tier.cta}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <section className="relative py-16 md:py-20 bg-[#0A0A0F]" data-testid="tier-includes">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10">
          <p className="eyebrow gradient-text">What's included</p>
          <ul className="mt-6 grid sm:grid-cols-2 gap-4">
            {tier.features.map((f) => (
              <li key={f} className="flex items-start gap-3 rounded-2xl border border-white/5 bg-surface p-5">
                <Check className="w-4 h-4 mt-1 shrink-0" style={{ color: tier.color }} />
                <span className="text-silver/85 text-sm md:text-base">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {tier.nextSteps && (
        <section className="relative py-16 md:py-20 bg-void" data-testid="tier-next-steps">
          <div className="max-w-[1000px] mx-auto px-5 md:px-10">
            <p className="eyebrow gradient-text">What happens next</p>
            <ol className="mt-6 space-y-4">
              {tier.nextSteps.map((s, i) => (
                <li key={s} className="flex items-start gap-4">
                  <span className="w-8 h-8 shrink-0 rounded-full grid place-items-center gradient-bg text-white font-serif text-sm">
                    {i + 1}
                  </span>
                  <span className="text-silver/85 text-sm md:text-base pt-1 leading-relaxed">{s}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}

      <section className="relative py-16 md:py-20 bg-[#0A0A0F]" data-testid="tier-proof">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10">
          <p className="eyebrow gradient-text">Proof it works</p>
          <div className="mt-6 grid md:grid-cols-2 gap-6">
            {relevantCases.map((c) => (
              <div key={c.car} className="rounded-3xl border border-white/5 bg-surface p-7">
                <p className="eyebrow gradient-text">{c.city}</p>
                <h3 className="mt-2 font-serif text-2xl text-white">{c.car}</h3>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="text-silver/50 line-through text-sm">{formatINR(c.dealer)}</span>
                  <span className="font-serif text-2xl text-white">{formatINR(c.final)}</span>
                </div>
                <p className="mt-3 text-xs uppercase tracking-wider gradient-text">Saved {formatINR(c.saved)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {relevantFaqs.length > 0 && (
        <section className="relative py-16 md:py-20 bg-void" data-testid="tier-faq">
          <div className="max-w-[1000px] mx-auto px-5 md:px-10">
            <p className="eyebrow gradient-text">Questions about {tier.name}</p>
            <div className="mt-6 space-y-6">
              {relevantFaqs.map((f) => (
                <div key={f.q} className="border-b border-white/5 pb-6">
                  <p className="font-serif text-lg md:text-xl text-white">{f.q}</p>
                  <p className="mt-2 text-sm text-silver/70 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="relative py-20 md:py-28 bg-[#0A0A0F] text-center" data-testid="tier-final-cta">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10">
          <p className="font-serif text-3xl md:text-5xl text-white leading-tight">
            Ready to <span className="italic gradient-text">{tier.cta.toLowerCase()}?</span>
          </p>
          <button
            onClick={() => openWhatsApp({ source: `tier_page_final_${tier.id}`, tier: tier.name })}
            className="mt-8 inline-flex items-center gap-2.5 px-7 py-4 rounded-full text-sm md:text-base font-medium text-white gradient-bg shadow-[0_20px_60px_-15px_rgba(139,92,246,0.6)] hover:scale-[1.03] transition-transform"
          >
            {tier.cta}
            <ArrowRight className="w-4 h-4" />
          </button>
          <p className="mt-6 text-sm text-silver/50">
            Not sure this is the right fit? <Link to="/#which-saarthi" className="underline hover:text-white">See which Saarthi you need</Link>
          </p>
        </div>
      </section>

      <Footer />
      <StickyWhatsApp />
    </main>
  );
}
